import { ChangeEvent, FC, useState } from 'react'
import {
  BooleanInput, Create, ReferenceInput, SelectInput, SimpleForm,
  TextInput, required, useTranslate,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import LinkIcon from '@mui/icons-material/Link'
import AdvancedSettings from './AdvancedSettings'

const SpecUrlField: FC = () => {
  const translate = useTranslate()
  const toolSource = useWatch({ name: 'tool_source' }) as string | undefined
  const { setValue } = useFormContext()
  const [inputMode, setInputMode] = useState<'url' | 'file'>('url')
  const [fileName, setFileName] = useState<string | null>(null)

  if (toolSource !== 'openapi' && toolSource !== 'odata') return null

  const label = toolSource === 'odata'
    ? translate('data.odataUrl')
    : translate('data.openapiUrl')
  const helperText = toolSource === 'odata'
    ? translate('data.odataUrlHelp')
    : translate('data.openapiUrlHelp')

  const handleModeChange = (_: unknown, newMode: 'url' | 'file' | null) => {
    if (!newMode) return
    setInputMode(newMode)
    if (newMode === 'url') {
      setValue('openapi_content', undefined)
      setFileName(null)
    } else {
      setValue('openapi_url', undefined)
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (event) => {
      setValue('openapi_content', event.target?.result as string)
    }
    reader.readAsText(file)
  }

  const accept = toolSource === 'odata' ? '.xml' : '.json,.yaml,.yml'

  return (
    <Box sx={{ width: '100%', mb: 1 }}>
      <ToggleButtonGroup
        value={inputMode}
        exclusive
        onChange={handleModeChange}
        size="small"
        sx={{ mb: 1 }}
      >
        <ToggleButton value="url">
          <LinkIcon sx={{ mr: 0.5 }} fontSize="small" />
          {translate('data.specInputUrl')}
        </ToggleButton>
        <ToggleButton value="file">
          <UploadFileIcon sx={{ mr: 0.5 }} fontSize="small" />
          {translate('data.specInputFile')}
        </ToggleButton>
      </ToggleButtonGroup>

      {inputMode === 'url' ? (
        <TextInput
          source="openapi_url"
          label={label}
          fullWidth
          validate={required()}
          helperText={helperText}
        />
      ) : (
        <Box>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
          >
            {translate('data.selectSpecFile')}
            <input
              type="file"
              hidden
              accept={accept}
              onChange={handleFileChange}
            />
          </Button>
          {fileName && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {fileName}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

const McpServerCreate: FC = () => {
  const translate = useTranslate()

  return (
    <Create redirect="show">
      {/* eslint-disable-next-line camelcase */}
      <SimpleForm defaultValues={{ enabled: true, tool_source: 'openapi' }}>
        <TextInput source="display_name" label="data.displayName" fullWidth validate={required()} />
        <TextInput source="description" label="data.description" fullWidth multiline />
        <ReferenceInput source="application_id" reference="application">
          <SelectInput optionText="display_name" label="data.application" fullWidth validate={required()} />
        </ReferenceInput>
        <SelectInput
          source="tool_source"
          label="data.toolSource"
          fullWidth
          validate={required()}
          choices={[
            { id: 'openapi', name: translate('data.toolSourceOpenapi') },
            { id: 'odata', name: translate('data.toolSourceOdata') },
            { id: 'manual', name: translate('data.toolSourceManual') },
          ]}
        />
        <SpecUrlField />
        <BooleanInput source="enabled" label="data.enabled" defaultValue={true} />
        <BooleanInput source="marketplace" label="data.marketplaceVisible" defaultValue={false} />
        <TextInput
          source="about"
          label="data.mcpAbout"
          fullWidth
          multiline
          rows={4}
        />
        <TextInput
          source="project_instructions"
          label="data.projectInstructions"
          fullWidth
          multiline
          rows={6}
          helperText={translate('data.projectInstructionsHelp')}
        />
        <AdvancedSettings />
      </SimpleForm>
    </Create>
  )
}

export default McpServerCreate
